/**
 * Creates a single-flight brainstorm saver that prevents duplicate card creation
 * and ensures only the newest state is persisted when multiple saves overlap.
 *
 * Guards:
 * 1. createInFlightRef — single-flight gate prevents duplicate creates
 * 2. saveVersion — monotonic counter ensures only the latest caller updates
 */
export function createBrainstormSaver({
  fetchWithIdToken,
  onCardCreated,
  onSaveStart,
  onSaveComplete,
  onError,
  unmountedRef,
  cardIdRef,
  createInFlightRef,
}) {
  let saveVersion = 0;
  let sessionGeneration = 0;
  let updateInFlight = null;

  function invalidate() {
    ++saveVersion;
    ++sessionGeneration;
    createInFlightRef.current = null;
    updateInFlight = null;
  }

  async function saveImmediate({
    starters,
    rejected,
    context,
    keepers,
    prompt,
    startTime,
    endTime,
    list,
    needsSaveRef,
    documentId,
  }) {
    if (unmountedRef.current) return;

    // Take a version number — only the highest-versioned caller should update
    const myVersion = ++saveVersion;

    // Wait for any in-flight create to complete first.
    if (createInFlightRef.current) {
      await createInFlightRef.current;
      if (unmountedRef.current) return;
      // If a newer caller entered while we waited, our data is stale — bail out.
      // The newer caller will persist the latest state.
      if (myVersion !== saveVersion) return;
    }

    // Re-check unmounted after any async gap
    if (unmountedRef.current) return;

    // Use the live cardId ref, not stale React state
    const currentCardId = cardIdRef.current;

    onSaveStart();

    const cardData = {
      items: keepers,
      prompt: prompt,
      documentId: documentId,
    };

    const brainstormData = {
      keepers: keepers,
      starters: starters,
      rejected: rejected,
      context: context,
      sessionStart: startTime,
      sessionEnd: endTime,
      exampleList: list,
    };

    const myGeneration = sessionGeneration;

    if (currentCardId) {
      // UPDATE path — serialize updates so only one is in flight at a time.
      // This prevents out-of-order completion from overwriting newer data.
      if (updateInFlight) {
        await updateInFlight;
        if (unmountedRef.current) return;
        if (myVersion !== saveVersion) return;
      }
      if (myVersion !== saveVersion) return;
      const updatePromise = (async () => {
        try {
          await fetchWithIdToken(`/api/brainstorm/update/${currentCardId}`, {
            ...cardData,
            ...brainstormData,
          });
          if (myGeneration !== sessionGeneration) return;
          onSaveComplete();
        } catch (err) {
          console.log(err);
          if (myGeneration !== sessionGeneration) return;
          onError("Something went wrong. Try again in a minute or so?");
        } finally {
          if (updateInFlight === updatePromise) {
            updateInFlight = null;
          }
        }
      })();
      updateInFlight = updatePromise;
      await updatePromise;
    } else {
      // CREATE path — single-flight guard
      const createPromise = (async () => {
        try {
          const result = await fetchWithIdToken("/api/brainstorm/create", {
            ...cardData,
            ...brainstormData,
          });
          if (myGeneration !== sessionGeneration) return;
          cardIdRef.current = result.id;
          onCardCreated(result.id);
          onSaveComplete();
        } catch (err) {
          console.log(err);
          if (myGeneration !== sessionGeneration) return;
          onError("Something went wrong. Try again in a minute or so?");
        } finally {
          if (createInFlightRef.current === createPromise) {
            createInFlightRef.current = null;
          }
        }
      })();
      createInFlightRef.current = createPromise;
      await createPromise;
    }

    if (myVersion === saveVersion) {
      needsSaveRef.current = false;
    }
  }

  return { saveImmediate, invalidate };
}
